// ÎNCERCĂRI PARCARE — ecranul de depanare al subsolului unei arhitecturi.
//
// Se ajunge de pe cardul arhitecturii. Pânza e viewer-ul întreg, ca în Studio
// (modul `plin`, deschis pe nivelul -1), cu straturile lui — «Alei — lățimea»,
// «Depanare — zone fără locuri» — și unealta «?» (de ce nu e loc aici). Peste
// el, în dreapta: soluția însăși și încercările ei (referința fără rampe,
// seturile de rampe pierzătoare — soluții `abandoned` copil, v. `service`), iar
// sub listă filmul împachetării, rampele și rafinarea cu model (jurnalul a tot
// ce a cerut modelul). Tot ce e aici vine din documentele deja salvate; nu se
// reia nicio rulare.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as api from "../api";
import Vizualizator from "../viewer/Vizualizator";
import { depanareDin, type CadruFilm } from "../viewer/plan/film";

/** Secundele unui pas, scurt: sub 10 s cu o zecimală, apoi întregi, peste un
 *  minut «m:ss». */
function durataScurta(s: number): string {
  if (s < 10) return s.toFixed(1);
  if (s < 60) return String(Math.round(s));
  const m = Math.floor(s / 60), r = Math.round(s - m * 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}
import RampeDepanare, { type RaportRampe } from "./RampeDepanare";
import RafinareDepanare, { type RaportRafinare } from "./RafinareDepanare";
import "../solutions/board.css";
import { listSolutions, type SolutieRand } from "../nav/api";
import { caleRand, numeRand } from "../nav/selectie";
import { Ic } from "../components/ui";
import { useT } from "../i18n";

type Doc = { diagnostics?: Record<string, unknown> | null; [k: string]: unknown };

/** Ora la care s-a calculat planșa unei încercări. Soluția-părinte își reface
 *  subsolul în loc la fiecare regenerare, deci pentru ea contează ULTIMA
 *  scriere (`updated_at`), nu crearea rândului; încercările se scriu o dată. */
function oraCalculului(s: SolutieRand): string {
  const iso = s.updated_at ?? s.created_at;
  if (!iso) return "";
  const d = new Date(iso);
  const azi = new Date();
  const ora = d.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" });
  const aceeasiZi = d.toDateString() === azi.toDateString();
  return aceeasiZi ? `calculată azi la ${ora}`
    : `calculată ${d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })} la ${ora}`;
}

export default function Incercari() {
  const t = useT();
  const { projectId = "", solutionId = "" } = useParams();
  const [solutions, setSolutions] = useState<SolutieRand[] | null>(null);
  const [alesId, setAlesId] = useState<string | null>(null);
  const [doc, setDoc] = useState<Doc | null>(null);
  const [eroare, setEroare] = useState("");

  useEffect(() => {
    // încercările de rampă sunt rânduri INTERNE: registrul le dă numai la
    // cerere (`?interne=1`, contract §3)
    listSolutions(projectId, { interne: true }).then((r) => setSolutions(r.solutii))
      .catch(() => setEroare("Nu am putut încărca soluțiile."));
  }, [projectId]);

  const parinte = solutions?.find((s) => s.id === solutionId) ?? null;
  const lista = useMemo(() => {
    // Fiecare regenerare a subsolului scrie iar încercările ei, cu aceleași
    // etichete; se arată doar ULTIMA de fiecare etichetă — cea a rulării
    // curente, adică a soluției care se vede.
    const ultima = new Map<string, SolutieRand>();
    for (const s of (solutions ?? []).filter(
        (x) => x.parent_solution_id === solutionId && x.summary.rol === "incercare")) {
      const k = s.summary.eticheta ?? s.id;
      const v = ultima.get(k);
      if (!v || (s.created_at ?? "") >= (v.created_at ?? "")) ultima.set(k, s);
    }
    const copii = [...ultima.values()]
      .sort((a, b) => (a.summary.eticheta ?? "").localeCompare(b.summary.eticheta ?? ""));
    return parinte ? [parinte, ...copii] : copii;
  }, [solutions, solutionId, parinte]);
  const ales = lista.find((s) => s.id === (alesId ?? solutionId)) ?? lista[0] ?? null;

  useEffect(() => {
    setDoc(null);
    if (!ales) return;
    let viu = true;
    api.solutionDocument(projectId, ales.id).then((d) => viu && setDoc(d as Doc))
      .catch(() => viu && setEroare("Nu am putut încărca documentul încercării."));
    return () => { viu = false; };
  }, [projectId, ales?.id]);

  const dep = useMemo(() => depanareDin(doc?.diagnostics), [doc]);
  // raportul căutării de rampe (modulul nou), așa cum l-a scris `benzi.adaptor`:
  // fie la rădăcina diagnosticelor (încercări), fie pe structura din schemă
  const raportRampe = useMemo(() => {
    const diag = doc?.diagnostics as Record<string, unknown> | undefined;
    if (diag?.rampe) return diag.rampe as RaportRampe;
    const structuri = (diag?.structures ?? []) as { rampe?: RaportRampe }[];
    return structuri.find((r) => r.rampe)?.rampe ?? null;
  }, [doc]);
  // RAFINAREA CU MODEL (`rafinare_llm.pas`): raportul cu jurnalul modelului, la
  // rădăcina diagnosticelor (încercări) sau pe structura din schemă
  const raportRafinare = useMemo(() => {
    const diag = doc?.diagnostics as Record<string, unknown> | undefined;
    if (diag?.rafinare_llm) return diag.rafinare_llm as RaportRafinare;
    const structuri = (diag?.structures ?? []) as { rafinare_llm?: RaportRafinare }[];
    return structuri.find((r) => r.rafinare_llm)?.rafinare_llm ?? null;
  }, [doc]);
  // FILMUL ÎMPACHETĂRII: un cadru pe pas, ales din listă. `null` = filmul e
  // stins și se vede planșa finită, cum era ecranul înainte.
  const [cadru, setCadru] = useState<number | null>(null);
  useEffect(() => { setCadru(null); }, [ales?.id]);
  const film = dep.film;
  const cadruAles: CadruFilm | null =
    cadru != null && cadru < film.length ? film[cadru] : null;

  return (
    <div className="studio incercari">
      {ales && <Vizualizator key={ales.id} projectId={projectId} solution={ales} plin
                             nivelInitial={-1} cadruFilm={cadruAles} depanare />}

      {/* PARKING INSPECTOR: numele uneltei sus, la mijloc, cu drumul înapoi.
          În stânga-sus stătea peste meniul vizualizatorului, pe eticheta
          «Depanare» (26.09.2026). */}
      <div className="float card ob-insp-titlu">
        <Ic n="bug" s={15} />
        <b>{t("bloc.incercari_parcare")}</b>
        <span className="sg-muted">· {parinte ? numeRand(parinte) : "…"}</span>
        <Link to={parinte ? caleRand(projectId, parinte) : `/p/${projectId}/solutii`}>
          ← {parinte ? numeRand(parinte) : "Soluții"}
        </Link>
      </div>

      <div className="float card ob-incercari-panou" style={{ right: 16, top: 56 }}>
        {eroare && <p className="err">{eroare}</p>}
        <div className="lbl">Încercări · {lista.length}</div>
        <div className="ob-incercari-lista">
          {lista.map((s) => (
            <button key={s.id} className={`ob-incercare${s.id === ales?.id ? " on" : ""}`}
                    onClick={() => setAlesId(s.id)}>
              <span className="ob-incercare-nume">
                {s.id === solutionId ? `${numeRand(s)} (soluția)` : (s.summary.eticheta ?? s.label)}
              </span>
              <span className="mono sg-muted">
                {s.summary.locuri_parcare ?? "—"} locuri
                {s.summary.fezabil === false ? " · cu abateri" : ""}
              </span>
              <span className="mono sg-muted" title="când s-a calculat planșa asta">
                {oraCalculului(s)}
              </span>
            </button>
          ))}
          {solutions && !lista.length && <p className="sg-muted">Nicio încercare salvată.</p>}
        </div>

        <details open={film.length > 0}>
          <summary className="lbl">Filmul împachetării · {film.length} cadre</summary>
          {film.length ? (
            <>
              <div className="ob-film-cap">
                <button disabled={cadru == null || cadru === 0}
                        onClick={() => setCadru((c) => (c ?? 0) - 1)}>‹</button>
                <button disabled={cadru != null && cadru >= film.length - 1}
                        onClick={() => setCadru((c) => (c == null ? 0 : c + 1))}>›</button>
                <button onClick={() => setCadru(null)} disabled={cadru == null}>stinge</button>
                <span className="sg-muted">
                  {cadruAles ? `${(cadru ?? 0) + 1}/${film.length}` : "planșa finită"}
                </span>
              </div>
              <table className="mono ob-film">
                <thead><tr><th>pas</th><th>alei</th><th>+</th><th>−</th>
                  <th title="locurile puse de pas (violet pe desen)">+loc</th><th>locuri</th>
                  <th title="cât a durat pasul, în secunde">s</th></tr></thead>
                <tbody>
                  {film.map((c, i) => (
                    <tr key={i} className={i === cadru ? "castigator" : undefined}
                        style={{ cursor: "pointer" }} onClick={() => setCadru(i)}
                        title={[c.subsol && `subsol ${c.subsol}`, c.nota]
                                 .filter(Boolean).join(" · ") || undefined}>
                      <td>{c.pas}{c.nota === "ales" ? " ★" : ""}
                        {/* pagina de depanare a pasului (căile de evacuare), în filă nouă */}
                        {c.pagina && (
                          <a href={`/api/projects/${projectId}/solutions/${solutionId}/pagini/${c.pagina}`}
                             target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}
                             title="pagina de depanare a pasului, în filă nouă"
                             onClick={(e) => e.stopPropagation()}>detalii ↗</a>
                        )}
                      </td>
                      <td>{c.alei.length}</td>
                      <td>{c.adaugate || ""}</td>
                      {/* aleile scoase; la pasul ALA, locurile luate */}
                      <td>{c.pierdute?.length || c.scoase.length || ""}</td>
                      <td>{c.noi?.length || ""}</td>
                      <td>{c.locuri ?? "—"}</td>
                      <td className="ob-film-s">{c.durata_s != null ? durataScurta(c.durata_s) : ""}</td>
                    </tr>
                  ))}
                  {film.some((c) => c.durata_s != null) && (
                    <tr className="ob-film-total">
                      <td colSpan={6}>total pe cadrele cu ceas</td>
                      <td className="ob-film-s">
                        {durataScurta(film.reduce((t, c) => t + (c.durata_s ?? 0), 0))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <p className="sg-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Cât e ales un cadru, planșa finită își ascunde locurile și aleile: pe
                desen rămân platelajul, rampele, stâlpii, pereții și nucleele, plus starea
                pasului. Violet îngroșat: aleea adăugată de pasul ăsta. Punctat roșu: aleea
                scoasă de el. Verde: locurile pe care le purta rețeaua atunci. Pe fiecare alee,
                tooltipul spune din ce linie de schelet vine. Cadrele «început: …» sunt
                scheletele încercate, cu ★ pe cel ales; primul dintre ele e cel de
                dinaintea începuturilor, deci dacă el are ★ planul e cel dinainte.
              </p>
            </>
          ) : <p className="sg-muted">
                {doc ? "Documentul n-are filmul (rulare dinainte de 14.09.2026)." : "…"}
              </p>}
        </details>

        <details open>
          <summary className="lbl">
            Rampe · {raportRampe?.status === "ok"
              ? `${raportRampe.cost} locuri pierdute`
              : raportRampe ? "infezabil" : "—"}
          </summary>
          <RampeDepanare raport={raportRampe} doc={doc as never} />
        </details>

        <details open={raportRafinare?.status === "ok"}>
          <summary className="lbl">
            Rafinare cu model · {raportRafinare?.status === "ok"
              ? `${(raportRafinare.locuri_dupa ?? 0) - (raportRafinare.locuri_inainte ?? 0) >= 0 ? "+" : ""}${
                  (raportRafinare.locuri_dupa ?? 0) - (raportRafinare.locuri_inainte ?? 0)} locuri`
              : raportRafinare ? raportRafinare.status : "—"}
          </summary>
          <RafinareDepanare raport={raportRafinare} />
        </details>
      </div>
    </div>
  );
}
